/**
 * Página /admin/validaciones — S13-32/33 · reescrita S19-A2/A4 · tabs S21.
 *
 * Vista de la Comisión para validar lo que el vecino envía desde el portal.
 *
 * S21 — pestañas como en Solicitudes:
 * - **Estado** (primarias): En revisión / Validadas / No validadas / Todas.
 *   "En revisión" usa las tablas ricas interactivas (chips de validez,
 *   selección múltiple + validar en bloque, validar/rechazar/revisar). Las
 *   demás pestañas muestran historial **solo-lectura** (estado, quién/cuándo
 *   revisó, motivo de rechazo).
 * - **Tipo** (secundarias, chips): Todos / Residentes / Vehículos / Accesos /
 *   Recursos.
 *
 * S19-A4: la cola pasó de tarjetas a tablas con datos verificables y chips de
 * validez de formato (RUT mod11, email con @, celular 9 dígitos). El rechazo
 * sigue individual con motivo (`ModalRechazo`).
 *
 * Las solicitudes de recurso (flujo aprobar/rechazar con cascada) viven solo
 * en "En revisión"; su historial no se expone aún aquí.
 *
 * RBAC: ROLES_REVISAN_SOLICITUDES (comision).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { rechazarResidente, validarResidente } from '../../lib/api/residentes';
import {
  aprobarDesignacionAutorizado,
  rechazarDesignacionAutorizado,
} from '../../lib/api/validaciones';
import { DesignacionesAutorizado } from '../../components/admin/DesignacionesAutorizado';
import { rechazarVehiculo, validarVehiculo } from '../../lib/api/vehiculos';
import {
  rechazarCitofonia,
  rechazarWifi,
  validarCitofonia,
  validarWifi,
} from '../../lib/api/accesos';
import {
  aprobarSolicitudRecurso,
  listSolicitudesConDetalle,
  rechazarSolicitudRecurso,
} from '../../lib/api/solicitudes-recurso';
import {
  listarPendientes,
  listarValidables,
  type AccesoPendiente,
  type DesignacionAutorizadoPendiente,
  type FiltroEstadoValidacion,
  type ResidentePendiente,
  type VehiculoPendiente,
} from '../../lib/api/validaciones';
import type { SolicitudRecursoConDetalle } from '../../lib/api/types';
import {
  PARENTESCO_LABEL,
  RECURSO_TIPO_LABEL,
  TRACCION_LABEL,
} from '../../lib/labels';
import type { ParentescoLiteral } from '../../lib/api/residentes';
import type { TipoTraccion } from '../../lib/api/types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import {
  ModalRevisarResidente,
  ModalRevisarVehiculo,
} from '../../components/admin/ModalRevisar';
import {
  celularOk,
  emailOk,
  formatCelular,
  formatCLP,
  formatFecha,
  formatRut,
  rutOk,
} from '../../lib/format';
import { formatearFecha } from '../../lib/tiempo';

const FILTROS_ESTADO: { valor: FiltroEstadoValidacion; etiqueta: string }[] = [
  { valor: 'pendiente', etiqueta: 'En revisión' },
  { valor: 'validadas', etiqueta: 'Validadas' },
  { valor: 'rechazadas', etiqueta: 'No validadas' },
  { valor: 'todas', etiqueta: 'Todas' },
];

type FiltroTipo =
  | 'todos'
  | 'residentes'
  | 'vehiculos'
  | 'accesos'
  | 'recursos'
  | 'autorizados';
const FILTROS_TIPO: { valor: FiltroTipo; etiqueta: string }[] = [
  { valor: 'todos', etiqueta: 'Todos' },
  { valor: 'residentes', etiqueta: 'Residentes' },
  { valor: 'vehiculos', etiqueta: 'Vehículos' },
  { valor: 'accesos', etiqueta: 'Accesos' },
  { valor: 'recursos', etiqueta: 'Recursos' },
  { valor: 'autorizados', etiqueta: 'Autorizados' },
];

/** Vocabulario usuario-facing del estado (igual que MisDatos del vecino).
 *  Residentes/vehículos usan aprobado/rechazado; accesos usan activo/baja. */
const ESTADO_LABEL: Record<string, string> = {
  aprobado: 'Validado',
  activo: 'Validado',
  rechazado: 'No validado',
  baja: 'No validado',
  pendiente_validacion: 'En revisión',
  inactivo: 'Inactivo',
};

function useValidables(estado: FiltroEstadoValidacion, refreshKey: number) {
  const [residentes, setResidentes] = useState<ResidentePendiente[]>([]);
  const [vehiculos, setVehiculos] = useState<VehiculoPendiente[]>([]);
  const [citofonia, setCitofonia] = useState<AccesoPendiente[]>([]);
  const [wifi, setWifi] = useState<AccesoPendiente[]>([]);
  const [autorizados, setAutorizados] = useState<
    DesignacionAutorizadoPendiente[]
  >([]);
  const [solicitudes, setSolicitudes] = useState<SolicitudRecursoConDetalle[]>(
    [],
  );
  // Todas las solicitudes ACTIVAS para advertir cascada al rechazar.
  const [solicitudesActivas, setSolicitudesActivas] = useState<
    SolicitudRecursoConDetalle[]
  >([]);
  // Historial de solicitudes de recurso (no pendientes) para las pestañas
  // Validadas / No validadas / Todas.
  const [solicitudesHistorial, setSolicitudesHistorial] = useState<
    SolicitudRecursoConDetalle[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);

    const esPendiente = estado === 'pendiente';
    // En "En revisión" traemos las solicitudes ACTIVAS (pendientes + aviso de
    // cascada). En historial (Validadas / No validadas / Todas) traemos TODAS
    // para poder mostrar las ya resueltas.
    const cargaValidables = esPendiente
      ? listarPendientes()
      : listarValidables(estado);
    const cargaSolicitudes = esPendiente
      ? listSolicitudesConDetalle({ activas: true }).catch(
          () => [] as SolicitudRecursoConDetalle[],
        )
      : listSolicitudesConDetalle({}).catch(
          () => [] as SolicitudRecursoConDetalle[],
        );

    Promise.all([cargaValidables, cargaSolicitudes])
      .then(([validables, solicitudesTodas]) => {
        if (cancelado) return;
        setResidentes(validables.residentes);
        setVehiculos(validables.vehiculos);
        setCitofonia(validables.citofonia);
        setWifi(validables.wifi);
        setAutorizados(validables.autorizados);
        setSolicitudes(
          solicitudesTodas.filter((s) => s.estado === 'pendiente_aprobacion'),
        );
        setSolicitudesActivas(solicitudesTodas);
        // Historial: las no pendientes de aprobación.
        setSolicitudesHistorial(
          solicitudesTodas.filter((s) => s.estado !== 'pendiente_aprobacion'),
        );
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar validaciones`
            : 'Error de red al cargar validaciones',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [estado, refreshKey]);

  return {
    residentes,
    vehiculos,
    citofonia,
    wifi,
    autorizados,
    solicitudes,
    solicitudesActivas,
    solicitudesHistorial,
    loading,
    error,
  };
}

// Agrupa el estado de una solicitud de recurso en validada / no_validada para
// las pestañas de historial de la Comisión.
const ESTADOS_SOLICITUD_VALIDADA = new Set([
  'aprobada',
  'esperando_cobro',
  'esperando_retiro',
  'esperando_asignacion',
  'asignada',
]);
const ESTADOS_SOLICITUD_NO_VALIDADA = new Set([
  'rechazada',
  'cancelada',
  'cancelada_por_cascada',
]);

const LABEL_ESTADO_SOLICITUD: Record<string, string> = {
  aprobada: 'Aprobada',
  esperando_cobro: 'Esperando cobro',
  esperando_retiro: 'Esperando retiro',
  esperando_asignacion: 'Esperando asignación',
  asignada: 'Entregada',
  rechazada: 'Rechazada',
  cancelada: 'Cancelada',
  cancelada_por_cascada: 'Cancelada (en cascada)',
};

/** Chip ✓/⚠ que marca si un dato cumple el formato esperado. */
function ChipValidez({ ok }: { ok: boolean }) {
  return (
    <span
      className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] leading-none ${
        ok
          ? 'bg-state-success/20 text-state-success'
          : 'bg-state-warning/20 text-state-warning'
      }`}
      title={ok ? 'Formato válido' : 'Formato sospechoso — revisar'}
      aria-label={ok ? 'formato válido' : 'formato sospechoso, revisar'}
    >
      {ok ? '✓' : '⚠'}
    </span>
  );
}

/** Celda de dato verificable: valor formateado + chip de validez. */
function CeldaVerificable({
  valor,
  ok,
  formato,
}: {
  valor: string | null;
  ok: boolean;
  formato?: (v: string) => string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={valor ? 'text-text-primary' : 'text-text-muted'}>
        {valor ? (formato ? formato(valor) : valor) : '—'}
      </span>
      <ChipValidez ok={ok} />
    </div>
  );
}

/** Badge de estado para el historial (solo-lectura). */
function BadgeEstado({ estado }: { estado: string }) {
  const cls =
    estado === 'aprobado' || estado === 'activo'
      ? 'bg-state-success/20 text-state-success'
      : estado === 'rechazado' || estado === 'baja'
        ? 'bg-state-danger/20 text-state-danger'
        : 'bg-state-warning/20 text-state-warning';
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] whitespace-nowrap ${cls}`}
    >
      {ESTADO_LABEL[estado] ?? estado}
    </span>
  );
}

const TH = 'text-left font-medium py-2 px-2';
const TD = 'py-2 px-2 align-top';

interface ModalRechazoProps {
  titulo: string;
  onConfirmar: (motivo: string) => Promise<void>;
  onCerrar: () => void;
}

function ModalRechazo({ titulo, onConfirmar, onCerrar }: ModalRechazoProps) {
  const [motivo, setMotivo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!motivo.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirmar(motivo.trim());
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4"
      >
        <h2 className="text-lg font-medium text-text-primary">{titulo}</h2>
        <p className="text-sm text-text-secondary">
          El motivo será visible para el vecino para que entienda qué pasó.
        </p>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Ej: Falta comprobante de identidad, datos incorrectos…"
          className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          required
        />
        {error && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            type="submit"
            disabled={!motivo.trim() || submitting}
          >
            {submitting ? 'Enviando…' : 'Confirmar rechazo'}
          </Button>
        </div>
      </form>
    </div>
  );
}

type ModalRechazoEstado =
  | { tipo: 'residente'; id: string; nombre: string }
  | { tipo: 'vehiculo'; id: string; patente: string }
  | { tipo: 'citofonia'; id: string; resumen: string }
  | { tipo: 'wifi'; id: string; resumen: string }
  | { tipo: 'solicitud'; id: string; resumen: string }
  | null;

/** Clave de selección estable por tipo+id. */
type TipoValidable = 'residente' | 'vehiculo' | 'citofonia' | 'wifi';
function claveSel(tipo: TipoValidable, id: string): string {
  return `${tipo}:${id}`;
}

/** Fila normalizada para las pestañas de historial (solo-lectura). */
interface FilaHistorial {
  key: string;
  tipoLabel: string;
  casa: string;
  detalle: string;
  estado: string;
  validado_at: string | null;
  validado_por_nombre: string | null;
  motivo_rechazo: string | null;
  motivo_pendiente: string | null;
}

export function Validaciones() {
  const [filtroEstado, setFiltroEstado] =
    useState<FiltroEstadoValidacion>('pendiente');
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('todos');
  const [refreshKey, setRefreshKey] = useState(0);
  const {
    residentes,
    vehiculos,
    citofonia,
    wifi,
    autorizados,
    solicitudes,
    solicitudesActivas,
    solicitudesHistorial,
    loading,
    error,
  } = useValidables(filtroEstado, refreshKey);
  const [accionError, setAccionError] = useState<string | null>(null);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [validandoMasivo, setValidandoMasivo] = useState(false);

  const esRevision = filtroEstado === 'pendiente';
  const verResidentes = filtroTipo === 'todos' || filtroTipo === 'residentes';
  const verVehiculos = filtroTipo === 'todos' || filtroTipo === 'vehiculos';
  const verAccesos = filtroTipo === 'todos' || filtroTipo === 'accesos';
  const verRecursos = filtroTipo === 'todos' || filtroTipo === 'recursos';

  // Historial de solicitudes de recurso para la pestaña actual (Validadas /
  // No validadas / Todas). Vacío en "En revisión" (ahí van las pendientes).
  const solicitudesRecursoHistorial = useMemo(() => {
    if (esRevision) return [];
    return solicitudesHistorial.filter((s) => {
      if (filtroEstado === 'validadas')
        return ESTADOS_SOLICITUD_VALIDADA.has(s.estado);
      if (filtroEstado === 'rechazadas')
        return ESTADOS_SOLICITUD_NO_VALIDADA.has(s.estado);
      return true; // 'todas'
    });
  }, [esRevision, filtroEstado, solicitudesHistorial]);

  const solicitudesPorResidente = useMemo(() => {
    const map = new Map<string, SolicitudRecursoConDetalle[]>();
    for (const s of solicitudesActivas) {
      const arr = map.get(s.residente_id) ?? [];
      arr.push(s);
      map.set(s.residente_id, arr);
    }
    return map;
  }, [solicitudesActivas]);
  const solicitudesPorVehiculo = useMemo(() => {
    const map = new Map<string, SolicitudRecursoConDetalle[]>();
    for (const s of solicitudesActivas) {
      if (!s.vehiculo_id) continue;
      const arr = map.get(s.vehiculo_id) ?? [];
      arr.push(s);
      map.set(s.vehiculo_id, arr);
    }
    return map;
  }, [solicitudesActivas]);

  const [modalRechazo, setModalRechazo] = useState<ModalRechazoEstado>(null);
  // Modal "Revisar": la Comisión corrige un pendiente antes de validarlo.
  const [revisando, setRevisando] = useState<
    | { tipo: 'residente'; item: ResidentePendiente }
    | { tipo: 'vehiculo'; item: VehiculoPendiente }
    | null
  >(null);

  const accesos = useMemo(() => [...citofonia, ...wifi], [citofonia, wifi]);

  // Al refrescar la data, descartar de la selección lo que ya no esté.
  useEffect(() => {
    const vigentes = new Set<string>([
      ...residentes.map((r) => claveSel('residente', r.id)),
      ...vehiculos.map((v) => claveSel('vehiculo', v.id)),
      ...citofonia.map((a) => claveSel('citofonia', a.id)),
      ...wifi.map((a) => claveSel('wifi', a.id)),
    ]);
    setSeleccion((prev) => {
      const next = new Set([...prev].filter((k) => vigentes.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [residentes, vehiculos, citofonia, wifi]);

  // Cambiar de pestaña de estado limpia la selección (solo aplica a revisión).
  useEffect(() => {
    setSeleccion(new Set());
    setAccionError(null);
  }, [filtroEstado]);

  function toggle(clave: string) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });
  }
  function toggleVarios(claves: string[], seleccionar: boolean) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      for (const k of claves) {
        if (seleccionar) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  }

  async function ejecutarAccion(fn: () => Promise<unknown>) {
    setAccionError(null);
    try {
      await fn();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setAccionError(
        err instanceof ApiError
          ? `No se pudo completar la acción (error ${err.status}).`
          : 'Error de red al ejecutar la acción.',
      );
    }
  }

  // Validación masiva: cada clave seleccionada llama a su validador individual
  // en paralelo (Promise.allSettled). No hay endpoint masivo backend (D-S19-5).
  async function validarSeleccionados() {
    if (seleccion.size === 0 || validandoMasivo) return;
    setValidandoMasivo(true);
    setAccionError(null);
    const tareas = [...seleccion].map((clave) => {
      const sep = clave.indexOf(':');
      const tipo = clave.slice(0, sep) as TipoValidable;
      const id = clave.slice(sep + 1);
      switch (tipo) {
        case 'residente':
          return validarResidente(id);
        case 'vehiculo':
          return validarVehiculo(id);
        case 'citofonia':
          return validarCitofonia(id);
        case 'wifi':
          return validarWifi(id);
      }
    });
    const resultados = await Promise.allSettled(tareas);
    const fallidos = resultados.filter((r) => r.status === 'rejected').length;
    if (fallidos > 0) {
      setAccionError(
        `${fallidos} de ${resultados.length} no se pudieron validar. ` +
          'Revisa los datos e intenta de nuevo.',
      );
    }
    setSeleccion(new Set());
    setValidandoMasivo(false);
    setRefreshKey((k) => k + 1);
  }

  // Helpers de "seleccionar todo" por tabla.
  const clavesResidentes = residentes.map((r) => claveSel('residente', r.id));
  const clavesVehiculos = vehiculos.map((v) => claveSel('vehiculo', v.id));
  const clavesAccesos = accesos.map((a) => claveSel(a.tipo, a.id));
  const todosResSel =
    clavesResidentes.length > 0 &&
    clavesResidentes.every((k) => seleccion.has(k));
  const todosVehSel =
    clavesVehiculos.length > 0 && clavesVehiculos.every((k) => seleccion.has(k));
  const todosAccSel =
    clavesAccesos.length > 0 && clavesAccesos.every((k) => seleccion.has(k));

  // Filas del historial (solo-lectura) según el filtro de tipo.
  const filasHistorial = useMemo<FilaHistorial[]>(() => {
    const filas: FilaHistorial[] = [];
    if (verResidentes) {
      for (const r of residentes) {
        filas.push({
          key: `residente:${r.id}`,
          tipoLabel: 'Residente',
          casa: r.lote_identificador,
          detalle:
            r.persona_nombre_completo +
            ' · ' +
            (PARENTESCO_LABEL[r.parentesco as ParentescoLiteral] ??
              r.parentesco),
          estado: r.estado,
          validado_at: r.validado_at,
          validado_por_nombre: r.validado_por_nombre,
          motivo_rechazo: r.motivo_rechazo,
          motivo_pendiente: r.motivo_pendiente,
        });
      }
    }
    if (verVehiculos) {
      for (const v of vehiculos) {
        filas.push({
          key: `vehiculo:${v.id}`,
          tipoLabel: 'Vehículo',
          casa: v.lote_identificador,
          detalle: [v.patente, v.marca, v.modelo].filter(Boolean).join(' · '),
          estado: v.estado,
          validado_at: v.validado_at,
          validado_por_nombre: v.validado_por_nombre,
          motivo_rechazo: v.motivo_rechazo,
          motivo_pendiente: v.motivo_pendiente,
        });
      }
    }
    if (verAccesos) {
      for (const a of accesos) {
        filas.push({
          key: `${a.tipo}:${a.id}`,
          tipoLabel: a.tipo === 'citofonia' ? 'Citofonía' : 'Wifi',
          casa: a.lote_identificador,
          detalle:
            a.residente_nombre +
            (a.tipo === 'citofonia' && a.slot ? ` · slot ${a.slot}` : ''),
          estado: a.estado,
          validado_at: a.validado_at,
          validado_por_nombre: a.validado_por_nombre,
          motivo_rechazo: a.motivo_rechazo,
          motivo_pendiente: a.motivo_pendiente,
        });
      }
    }
    return filas.sort((x, y) => x.casa.localeCompare(y.casa));
  }, [residentes, vehiculos, accesos, verResidentes, verVehiculos, verAccesos]);

  return (
    <div className="px-4 md:px-8 py-6 space-y-5">
      <div>
        <h1 className="text-2xl font-medium text-text-primary">Validaciones</h1>
        <p className="text-sm text-text-secondary mt-1">
          Residentes, vehículos, accesos y recursos que los vecinos envían.
          Revisa los datos, valida o rechaza con motivo. En "En revisión" los
          chips <span className="text-state-warning">⚠</span> marcan formato
          sospechoso.
        </p>
      </div>

      {/* Tabs por estado (primarias). */}
      <div className="flex flex-wrap gap-2 border-b border-white/[0.08]">
        {FILTROS_ESTADO.map((f) => (
          <button
            key={f.valor}
            type="button"
            onClick={() => setFiltroEstado(f.valor)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              filtroEstado === f.valor
                ? 'border-orange text-text-primary font-medium'
                : 'border-transparent text-text-muted hover:text-text-secondary'
            }`}
          >
            {f.etiqueta}
          </button>
        ))}
      </div>

      {/* Filtro por tipo (secundario, chips). */}
      <div className="flex flex-wrap gap-2">
        {FILTROS_TIPO.map((f) => (
          <button
            key={f.valor}
            type="button"
            onClick={() => setFiltroTipo(f.valor)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              filtroTipo === f.valor
                ? 'border-orange text-orange bg-orange/10'
                : 'border-white/15 text-text-muted hover:text-text-secondary'
            }`}
          >
            {f.etiqueta}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {accionError && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {accionError}
        </div>
      )}

      {/* === Autorizados de portal (2026-09-03) ===
          Fuera de `RevisionInteractiva` a propósito: no participa de la
          selección masiva. Aprobar en lote algo que crea cuentas de portal
          invita a apretar sin leer, que es justo lo que este flujo evita.
          El propio componente se oculta si la lista viene vacía. */}
      {!loading && !error && (filtroTipo === 'todos' || filtroTipo === 'autorizados') && (
        <DesignacionesAutorizado
          designaciones={autorizados}
          onAprobar={async (id) => {
            await aprobarDesignacionAutorizado(id);
            setRefreshKey((k) => k + 1);
          }}
          onRechazar={async (id, motivo) => {
            await rechazarDesignacionAutorizado(id, motivo);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* ===================== En revisión (interactivo) ===================== */}
      {esRevision && !loading && !error && (
        <RevisionInteractiva
          residentes={residentes}
          vehiculos={vehiculos}
          accesos={accesos}
          solicitudes={solicitudes}
          verResidentes={verResidentes}
          verVehiculos={verVehiculos}
          verAccesos={verAccesos}
          verRecursos={verRecursos}
          seleccion={seleccion}
          validandoMasivo={validandoMasivo}
          todosResSel={todosResSel}
          todosVehSel={todosVehSel}
          todosAccSel={todosAccSel}
          clavesResidentes={clavesResidentes}
          clavesVehiculos={clavesVehiculos}
          clavesAccesos={clavesAccesos}
          solicitudesPorResidente={solicitudesPorResidente}
          solicitudesPorVehiculo={solicitudesPorVehiculo}
          onToggle={toggle}
          onToggleVarios={toggleVarios}
          onLimpiar={() => setSeleccion(new Set())}
          onValidarSeleccionados={validarSeleccionados}
          onValidar={(fn) => ejecutarAccion(fn)}
          onRechazar={setModalRechazo}
          onRevisarResidente={(r) => setRevisando({ tipo: 'residente', item: r })}
          onRevisarVehiculo={(v) => setRevisando({ tipo: 'vehiculo', item: v })}
        />
      )}

      {/* ===================== Historial (solo-lectura) ===================== */}
      {!esRevision && !loading && !error && (
        <>
          {filasHistorial.length === 0 &&
            (verRecursos ? solicitudesRecursoHistorial.length : 0) === 0 && (
              <Card>
                <p className="text-sm text-text-muted">
                  No hay registros para este filtro.
                </p>
              </Card>
            )}
          {filasHistorial.length > 0 && (
            <div className="overflow-x-auto border-[0.5px] border-white/[0.08] rounded-md">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                    <th className={TH}>Tipo</th>
                    <th className={TH}>Casa</th>
                    <th className={TH}>Detalle</th>
                    <th className={TH}>Estado</th>
                    <th className={TH}>Revisado</th>
                    <th className={TH}>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {filasHistorial.map((f) => (
                    <tr
                      key={f.key}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                    >
                      <td className={`${TD} text-text-secondary`}>
                        {f.tipoLabel}
                      </td>
                      <td className={`${TD} text-text-secondary`}>{f.casa}</td>
                      <td className={`${TD} text-text-primary`}>{f.detalle}</td>
                      <td className={TD}>
                        <BadgeEstado estado={f.estado} />
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {f.validado_at ? (
                          <>
                            {formatearFecha(f.validado_at)}
                            {f.validado_por_nombre && (
                              <div className="text-[11px] text-text-muted">
                                por {f.validado_por_nombre}
                              </div>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {f.motivo_rechazo || f.motivo_pendiente || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {verRecursos && solicitudesRecursoHistorial.length > 0 && (
            <div className="overflow-x-auto border-[0.5px] border-white/[0.08] rounded-md">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                    <th className={TH}>Recurso</th>
                    <th className={TH}>Casa</th>
                    <th className={TH}>Vecino</th>
                    <th className={TH}>Estado</th>
                    <th className={TH}>Revisado</th>
                    <th className={TH}>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitudesRecursoHistorial.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                    >
                      <td className={`${TD} text-text-secondary`}>
                        {RECURSO_TIPO_LABEL[s.tipo]}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {s.lote_identificador}
                      </td>
                      <td className={`${TD} text-text-primary`}>
                        {s.vecino_nombre}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {LABEL_ESTADO_SOLICITUD[s.estado] ?? s.estado}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {s.revisado_at ? formatearFecha(s.revisado_at) : '—'}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {s.motivo_rechazo ||
                          s.motivo_aprobacion ||
                          s.motivo_texto ||
                          '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {modalRechazo && (
        <ModalRechazo
          titulo={
            modalRechazo.tipo === 'residente'
              ? `No validar residente "${modalRechazo.nombre}"`
              : modalRechazo.tipo === 'vehiculo'
                ? `No validar vehículo ${modalRechazo.patente}`
                : modalRechazo.tipo === 'solicitud'
                  ? `No validar solicitud · ${modalRechazo.resumen}`
                  : `No validar ${modalRechazo.resumen}`
          }
          onConfirmar={async (motivo) => {
            const m = modalRechazo;
            if (m.tipo === 'residente') {
              await rechazarResidente(m.id, motivo);
            } else if (m.tipo === 'vehiculo') {
              await rechazarVehiculo(m.id, motivo);
            } else if (m.tipo === 'citofonia') {
              await rechazarCitofonia(m.id, motivo);
            } else if (m.tipo === 'wifi') {
              await rechazarWifi(m.id, motivo);
            } else {
              await rechazarSolicitudRecurso(m.id, motivo);
            }
            setModalRechazo(null);
            setRefreshKey((k) => k + 1);
          }}
          onCerrar={() => setModalRechazo(null)}
        />
      )}

      {revisando?.tipo === 'residente' && (
        <ModalRevisarResidente
          residente={revisando.item}
          onCerrar={() => setRevisando(null)}
          onListo={() => {
            setRevisando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
      {revisando?.tipo === 'vehiculo' && (
        <ModalRevisarVehiculo
          vehiculo={revisando.item}
          onCerrar={() => setRevisando(null)}
          onListo={() => {
            setRevisando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Sub-vista: pestaña "En revisión" (tablas interactivas).
// Extraída para que el componente principal no crezca sin control; el estado
// vive arriba y se pasa por props.
// ============================================================

interface RevisionInteractivaProps {
  residentes: ResidentePendiente[];
  vehiculos: VehiculoPendiente[];
  accesos: AccesoPendiente[];
  solicitudes: SolicitudRecursoConDetalle[];
  verResidentes: boolean;
  verVehiculos: boolean;
  verAccesos: boolean;
  verRecursos: boolean;
  seleccion: Set<string>;
  validandoMasivo: boolean;
  todosResSel: boolean;
  todosVehSel: boolean;
  todosAccSel: boolean;
  clavesResidentes: string[];
  clavesVehiculos: string[];
  clavesAccesos: string[];
  solicitudesPorResidente: Map<string, SolicitudRecursoConDetalle[]>;
  solicitudesPorVehiculo: Map<string, SolicitudRecursoConDetalle[]>;
  onToggle: (clave: string) => void;
  onToggleVarios: (claves: string[], seleccionar: boolean) => void;
  onLimpiar: () => void;
  onValidarSeleccionados: () => void;
  onValidar: (fn: () => Promise<unknown>) => void;
  onRechazar: (estado: ModalRechazoEstado) => void;
  onRevisarResidente: (r: ResidentePendiente) => void;
  onRevisarVehiculo: (v: VehiculoPendiente) => void;
}

function RevisionInteractiva({
  residentes,
  vehiculos,
  accesos,
  solicitudes,
  verResidentes,
  verVehiculos,
  verAccesos,
  verRecursos,
  seleccion,
  validandoMasivo,
  todosResSel,
  todosVehSel,
  todosAccSel,
  clavesResidentes,
  clavesVehiculos,
  clavesAccesos,
  solicitudesPorResidente,
  solicitudesPorVehiculo,
  onToggle,
  onToggleVarios,
  onLimpiar,
  onValidarSeleccionados,
  onValidar,
  onRechazar,
  onRevisarResidente,
  onRevisarVehiculo,
}: RevisionInteractivaProps) {
  const totalVisible =
    (verResidentes ? residentes.length : 0) +
    (verVehiculos ? vehiculos.length : 0) +
    (verAccesos ? accesos.length : 0) +
    (verRecursos ? solicitudes.length : 0);

  return (
    <div className="space-y-6">
      {/* Barra de validación masiva (selección global). */}
      {seleccion.size > 0 && (
        <div className="sticky top-2 z-10 flex items-center justify-between gap-3 bg-bg-card border-[0.5px] border-white/15 rounded-md px-4 py-2 shadow-lg">
          <span className="text-sm text-text-primary">
            {seleccion.size} seleccionado{seleccion.size === 1 ? '' : 's'}
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onLimpiar}
              disabled={validandoMasivo}
            >
              Limpiar selección
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onValidarSeleccionados}
              disabled={validandoMasivo}
            >
              {validandoMasivo
                ? 'Validando…'
                : `Validar ${seleccion.size} seleccionado${
                    seleccion.size === 1 ? '' : 's'
                  }`}
            </Button>
          </div>
        </div>
      )}

      {totalVisible === 0 && (
        <Card>
          <p className="text-sm text-text-muted">
            No hay nada pendiente de validación para este filtro.
          </p>
        </Card>
      )}

      {/* === Residentes === */}
      {verResidentes && residentes.length > 0 && (
        <section>
          <h2 className="text-lg font-medium text-text-primary mb-3">
            Residentes ({residentes.length})
          </h2>
          <div className="overflow-x-auto border-[0.5px] border-white/[0.08] rounded-md">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                  <th className="w-8 py-2 px-2">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todos los residentes"
                      checked={todosResSel}
                      onChange={(e) =>
                        onToggleVarios(clavesResidentes, e.target.checked)
                      }
                    />
                  </th>
                  <th className={TH}>Casa</th>
                  <th className={TH}>Nombre</th>
                  <th className={TH}>RUT</th>
                  <th className={TH}>Email</th>
                  <th className={TH}>Celular</th>
                  <th className={TH}>Parentesco</th>
                  <th className="text-right font-medium py-2 px-2 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {residentes.map((r) => {
                  const clave = claveSel('residente', r.id);
                  const cascadas = solicitudesPorResidente.get(r.id) ?? [];
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                    >
                      <td className={TD}>
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${r.persona_nombre_completo}`}
                          checked={seleccion.has(clave)}
                          onChange={() => onToggle(clave)}
                        />
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {r.lote_identificador}
                      </td>
                      <td className={`${TD} text-text-primary`}>
                        <div className="font-medium">
                          {r.persona_nombre_completo}
                        </div>
                        {r.motivo_pendiente && (
                          <div className="text-[11px] text-orange mt-0.5">
                            {r.motivo_pendiente}
                          </div>
                        )}
                        {cascadas.length > 0 && (
                          <div className="text-[11px] text-state-warning mt-0.5">
                            ⚠ {cascadas.length} solicitud
                            {cascadas.length === 1 ? '' : 'es'} de recurso se
                            cancelará{cascadas.length === 1 ? '' : 'n'} si
                            rechazas
                          </div>
                        )}
                      </td>
                      <td className={TD}>
                        <CeldaVerificable
                          valor={r.persona_rut}
                          ok={rutOk(r.persona_rut)}
                          formato={formatRut}
                        />
                      </td>
                      <td className={TD}>
                        <CeldaVerificable
                          valor={r.persona_email}
                          ok={emailOk(r.persona_email)}
                        />
                      </td>
                      <td className={TD}>
                        <CeldaVerificable
                          valor={r.persona_celular}
                          ok={celularOk(r.persona_celular)}
                          formato={formatCelular}
                        />
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {PARENTESCO_LABEL[r.parentesco as ParentescoLiteral] ??
                          r.parentesco}
                      </td>
                      <td className={`${TD} text-right whitespace-nowrap`}>
                        <button
                          type="button"
                          onClick={() => onRevisarResidente(r)}
                          className="text-xs text-text-secondary hover:underline mr-3"
                        >
                          Revisar
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            onRechazar({
                              tipo: 'residente',
                              id: r.id,
                              nombre: r.persona_nombre_completo,
                            })
                          }
                          className="text-xs text-state-danger hover:underline mr-3"
                        >
                          No validar
                        </button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onValidar(() => validarResidente(r.id))}
                        >
                          Validar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* === Vehículos === */}
      {verVehiculos && vehiculos.length > 0 && (
        <section>
          <h2 className="text-lg font-medium text-text-primary mb-3">
            Vehículos ({vehiculos.length})
          </h2>
          <div className="overflow-x-auto border-[0.5px] border-white/[0.08] rounded-md">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                  <th className="w-8 py-2 px-2">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todos los vehículos"
                      checked={todosVehSel}
                      onChange={(e) =>
                        onToggleVarios(clavesVehiculos, e.target.checked)
                      }
                    />
                  </th>
                  <th className={TH}>Casa</th>
                  <th className={TH}>Patente</th>
                  <th className={TH}>Marca</th>
                  <th className={TH}>Modelo</th>
                  <th className={TH}>Color</th>
                  <th className={TH}>Tracción</th>
                  <th className="text-right font-medium py-2 px-2 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {vehiculos.map((v) => {
                  const clave = claveSel('vehiculo', v.id);
                  const cascadas = solicitudesPorVehiculo.get(v.id) ?? [];
                  return (
                    <tr
                      key={v.id}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                    >
                      <td className={TD}>
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${v.patente}`}
                          checked={seleccion.has(clave)}
                          onChange={() => onToggle(clave)}
                        />
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {v.lote_identificador}
                      </td>
                      <td className={`${TD} text-text-primary font-mono`}>
                        <div>{v.patente}</div>
                        {v.motivo_pendiente && (
                          <div className="text-[11px] text-orange mt-0.5 font-sans">
                            {v.motivo_pendiente}
                          </div>
                        )}
                        {cascadas.length > 0 && (
                          <div className="text-[11px] text-state-warning mt-0.5 font-sans">
                            ⚠ {cascadas.length} solicitud
                            {cascadas.length === 1 ? '' : 'es'} de recurso se
                            cancelará{cascadas.length === 1 ? '' : 'n'} si
                            rechazas
                          </div>
                        )}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {v.marca || '—'}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {v.modelo || '—'}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {v.color || '—'}
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {TRACCION_LABEL[v.tipo_traccion as TipoTraccion] ??
                          v.tipo_traccion}
                      </td>
                      <td className={`${TD} text-right whitespace-nowrap`}>
                        <button
                          type="button"
                          onClick={() => onRevisarVehiculo(v)}
                          className="text-xs text-text-secondary hover:underline mr-3"
                        >
                          Revisar
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            onRechazar({
                              tipo: 'vehiculo',
                              id: v.id,
                              patente: v.patente,
                            })
                          }
                          className="text-xs text-state-danger hover:underline mr-3"
                        >
                          No validar
                        </button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onValidar(() => validarVehiculo(v.id))}
                        >
                          Validar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* === Accesos citofonía / wifi === */}
      {verAccesos && accesos.length > 0 && (
        <section>
          <h2 className="text-lg font-medium text-text-primary mb-3">
            Accesos ({accesos.length})
          </h2>
          <div className="overflow-x-auto border-[0.5px] border-white/[0.08] rounded-md">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                  <th className="w-8 py-2 px-2">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todos los accesos"
                      checked={todosAccSel}
                      onChange={(e) =>
                        onToggleVarios(clavesAccesos, e.target.checked)
                      }
                    />
                  </th>
                  <th className={TH}>Casa</th>
                  <th className={TH}>Residente</th>
                  <th className={TH}>Acceso</th>
                  <th className={TH}>Dato del acceso</th>
                  <th className={TH}>RUT (ficha)</th>
                  <th className={TH}>Email (ficha)</th>
                  <th className={TH}>Celular (ficha)</th>
                  <th className="text-right font-medium py-2 px-2 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {accesos.map((a) => {
                  const clave = claveSel(a.tipo, a.id);
                  const esCito = a.tipo === 'citofonia';
                  const acceso = esCito
                    ? `Citofonía · slot ${a.slot ?? '—'}`
                    : 'Wifi';
                  const snapValor = esCito
                    ? a.celular_snapshot
                    : a.email_snapshot;
                  const snapOk = esCito
                    ? celularOk(snapValor)
                    : emailOk(snapValor);
                  return (
                    <tr
                      key={a.id}
                      className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                    >
                      <td className={TD}>
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar acceso ${acceso} de casa ${a.lote_identificador}`}
                          checked={seleccion.has(clave)}
                          onChange={() => onToggle(clave)}
                        />
                      </td>
                      <td className={`${TD} text-text-secondary`}>
                        {a.lote_identificador}
                      </td>
                      <td className={`${TD} text-text-primary`}>
                        <div className="font-medium">{a.residente_nombre}</div>
                        {a.motivo_pendiente && (
                          <div className="text-[11px] text-orange mt-0.5">
                            {a.motivo_pendiente}
                          </div>
                        )}
                      </td>
                      <td className={`${TD} text-text-secondary`}>{acceso}</td>
                      <td className={TD}>
                        <CeldaVerificable
                          valor={snapValor}
                          ok={snapOk}
                          formato={esCito ? formatCelular : undefined}
                        />
                      </td>
                      <td className={TD}>
                        <CeldaVerificable
                          valor={a.residente_rut}
                          ok={rutOk(a.residente_rut)}
                          formato={formatRut}
                        />
                      </td>
                      <td className={TD}>
                        <CeldaVerificable
                          valor={a.residente_email}
                          ok={emailOk(a.residente_email)}
                        />
                      </td>
                      <td className={TD}>
                        <CeldaVerificable
                          valor={a.residente_celular}
                          ok={celularOk(a.residente_celular)}
                          formato={formatCelular}
                        />
                      </td>
                      <td className={`${TD} text-right whitespace-nowrap`}>
                        <button
                          type="button"
                          onClick={() =>
                            onRechazar({
                              tipo: esCito ? 'citofonia' : 'wifi',
                              id: a.id,
                              resumen: `${
                                esCito ? 'Citofonía' : 'Wifi'
                              } · Casa ${a.lote_identificador}`,
                            })
                          }
                          className="text-xs text-state-danger hover:underline mr-3"
                        >
                          No validar
                        </button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            onValidar(() =>
                              esCito
                                ? validarCitofonia(a.id)
                                : validarWifi(a.id),
                            )
                          }
                        >
                          Validar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* === Solicitudes de recurso (flujo aparte) === */}
      {verRecursos && solicitudes.length > 0 && (
        <section>
          <h2 className="text-lg font-medium text-text-primary mb-3">
            Solicitudes de recurso ({solicitudes.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {solicitudes.map((s) => (
              <Card key={s.id}>
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-base font-medium text-text-primary">
                      {s.tipo === 'llavero'
                        ? 'Llavero'
                        : s.tipo === 'control_remoto'
                          ? 'Control remoto'
                          : 'Otro recurso'}
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-text-muted">
                      {s.motivo === 'falla_fabrica' ? (
                        <span className="text-state-success">sin cobro</span>
                      ) : (
                        <span className="text-orange">cobro</span>
                      )}
                    </span>
                  </div>
                  <div className="text-xs text-text-muted mt-0.5">
                    Casa {s.lote_identificador} · {s.vecino_nombre}
                  </div>
                  <div className="text-xs text-text-secondary mt-1">
                    Motivo:{' '}
                    <span className="text-text-primary">{s.motivo}</span>
                    {s.motivo_texto && (
                      <span className="text-text-muted"> · {s.motivo_texto}</span>
                    )}
                  </div>
                  {s.monto_a_cobrar !== null && s.monto_a_cobrar > 0 && (
                    <div className="text-xs text-text-muted mt-1">
                      Monto sugerido (stock):{' '}
                      <span className="font-mono">
                        {formatCLP(s.monto_a_cobrar)}
                      </span>
                    </div>
                  )}
                  <div className="text-[11px] text-text-muted mt-1">
                    Solicitado: {formatFecha(s.solicitado_at.slice(0, 10))}
                  </div>
                  {s.notas && (
                    <div className="text-xs text-text-secondary italic mt-2">
                      {s.notas}
                    </div>
                  )}
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      onRechazar({
                        tipo: 'solicitud',
                        id: s.id,
                        resumen: `${s.tipo} · Casa ${s.lote_identificador}`,
                      })
                    }
                    className="text-xs text-state-danger hover:underline"
                  >
                    No validar
                  </button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onValidar(() => aprobarSolicitudRecurso(s.id))}
                  >
                    Validar
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
